import { ar } from './panel/ar.js';
import { en, type PanelCatalog } from './panel/en.js';

export { ar, en };
export type { PanelCatalog };

export const SUPPORTED_LANGUAGES = ['ar', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const RTL_LANGUAGES: ReadonlySet<SupportedLanguage> = new Set(['ar']);

export const panelResources: Record<SupportedLanguage, { translation: PanelCatalog }> = {
  ar: { translation: ar },
  en: { translation: en },
};

export function isSupportedLanguage(v: unknown): v is SupportedLanguage {
  return typeof v === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(v);
}

export function directionOf(lang: SupportedLanguage): 'rtl' | 'ltr' {
  return RTL_LANGUAGES.has(lang) ? 'rtl' : 'ltr';
}

/** Picks the best supported language from a list of BCP-47 tags (e.g. navigator.languages). */
export function negotiateLanguage(
  preferred: readonly string[],
  fallback: SupportedLanguage = 'en',
): SupportedLanguage {
  for (const tag of preferred) {
    const base = tag.toLowerCase().split('-')[0];
    if (isSupportedLanguage(base)) return base;
  }
  return fallback;
}

/** Flattens a nested catalog into dotted keys; used by tests and tooling. */
export function flattenCatalog(obj: object, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (typeof v === 'string') out[key] = v;
    else if (v && typeof v === 'object') Object.assign(out, flattenCatalog(v as object, key));
  }
  return out;
}
